import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from './firebase';
import { decrypt } from './webCryptoService';
import { getFileBlob } from './storageService';
import { encryptWithMasterPassword } from './masterPasswordCrypto';

/**
 * 데이터 내보내기 서비스
 * 패스워드, 신용카드, API키, 보안메모, 첨부파일 내보내기 지원
 */
export const exportService = {
  /**
   * 사용자의 모든 데이터 내보내기
   * @param {string} userId - 사용자 UID
   * @param {Object} exportConfig - 내보내기 설정
   * @returns {Promise<void>}
   */
  async exportUserData(userId, exportConfig) {
    try {
      const { items, format, includePasswords, encryption, masterPassword, timestamp } = exportConfig;
      const exportData = {
        metadata: {
          exportedAt: timestamp,
          exportedBy: userId,
          format,
          includePasswords,
          encryption,
          version: '2.0.0',
          compatibility: encryption === 'plain' ? 'universal' : 'password-wallet'
        },
        data: {}
      };

      // 각 데이터 타입별로 내보내기
      if (items.passwords) {
        exportData.data.passwords = await this.exportPasswords(userId, includePasswords);
      }

      if (items.creditCards) {
        exportData.data.creditCards = await this.exportCreditCards(userId, includePasswords);
      }

      if (items.apiKeys) {
        exportData.data.apiKeys = await this.exportApiKeys(userId, includePasswords);
      }

      if (items.secureNotes) {
        exportData.data.secureNotes = await this.exportSecureNotes(userId, items.attachments);
      }

      // 암호화 처리
      let finalData = exportData;
      if (encryption === 'master' && masterPassword) {
        const jsonString = JSON.stringify(exportData, null, 2);
        const encryptedData = await encryptWithMasterPassword(jsonString, masterPassword);
        finalData = {
          encrypted: true,
          version: '2.0.0',
          type: 'password-wallet-export',
          ...encryptedData
        };
      }

      // 파일 형식에 따라 다운로드
      if (format === 'json') {
        await this.downloadAsJSON(finalData, encryption === 'master');
      } else if (format === 'csv') {
        if (encryption === 'master') {
          throw new Error('CSV 형식은 마스터 패스워드 암호화를 지원하지 않습니다. JSON 형식을 사용하세요.');
        }
        await this.downloadAsCSV(finalData);
      }

    } catch (error) {
      console.error('데이터 내보내기 실패:', error);
      throw new Error('데이터 내보내기 중 오류가 발생했습니다.');
    }
  },

  /**
   * 패스워드 데이터 내보내기
   */
  async exportPasswords(userId, includePasswords) {
    try {
      const passwordsRef = collection(db, 'users', userId, 'passwords');
      const snapshot = await getDocs(passwordsRef);
      
      const passwords = [];
      
      for (const doc of snapshot.docs) {
        const data = doc.data();
        
        try {
          // 데이터 복호화
          const decryptedData = {
            id: doc.id,
            site: data.site,
            username: data.username ? await decrypt(data.username, userId) : '',
            email: data.email ? await decrypt(data.email, userId) : '',
            url: data.url || '',
            category: data.category || 'other',
            notes: data.notes ? await decrypt(data.notes, userId) : '',
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
            lastUsed: data.lastUsed,
            strength: data.strength,
            tags: data.tags || []
          };

          // 패스워드 처리
          if (includePasswords && data.password) {
            decryptedData.password = await decrypt(data.password, userId);
          } else {
            decryptedData.password = '****';
          }

          passwords.push(decryptedData);
        } catch (decryptError) {
          console.error('패스워드 복호화 실패:', decryptError);
          // 복호화 실패한 항목은 제외하고 계속
        }
      }

      return passwords;
    } catch (error) {
      console.error('패스워드 내보내기 실패:', error);
      return [];
    }
  },

  /**
   * 신용카드 데이터 내보내기
   */
  async exportCreditCards(userId, includePasswords) {
    try {
      const cardsRef = collection(db, 'users', userId, 'creditCards');
      const snapshot = await getDocs(cardsRef);
      
      const creditCards = [];
      
      for (const doc of snapshot.docs) {
        const data = doc.data();
        
        try {
          const decryptedData = {
            id: doc.id,
            cardholderName: data.cardholderName ? await decrypt(data.cardholderName, userId) : '',
            bankName: data.bankName || '',
            cardType: data.cardType || '',
            notes: data.notes ? await decrypt(data.notes, userId) : '',
            createdAt: data.createdAt,
            updatedAt: data.updatedAt
          };

          // 민감한 정보 처리
          if (includePasswords) {
            decryptedData.cardNumber = data.cardNumber ? await decrypt(data.cardNumber, userId) : '';
            decryptedData.expiryDate = data.expiryDate ? await decrypt(data.expiryDate, userId) : '';
            decryptedData.cvv = data.cvv ? await decrypt(data.cvv, userId) : '';
            decryptedData.pin = data.pin ? await decrypt(data.pin, userId) : '';
          } else {
            decryptedData.cardNumber = '****';
            decryptedData.expiryDate = '****';
            decryptedData.cvv = '***';
            decryptedData.pin = '****';
          }

          creditCards.push(decryptedData);
        } catch (decryptError) {
          console.error('신용카드 복호화 실패:', decryptError);
        }
      }

      return creditCards;
    } catch (error) {
      console.error('신용카드 내보내기 실패:', error);
      return [];
    }
  },

  /**
   * API 키 데이터 내보내기
   */
  async exportApiKeys(userId, includePasswords) {
    try {
      const apiKeysRef = collection(db, 'users', userId, 'apiKeys');
      const snapshot = await getDocs(apiKeysRef);
      
      const apiKeys = [];
      
      for (const doc of snapshot.docs) {
        const data = doc.data();
        
        try {
          const decryptedData = {
            id: doc.id,
            serviceName: data.serviceName,
            keyName: data.keyName,
            description: data.description || '',
            url: data.url || '',
            notes: data.notes ? await decrypt(data.notes, userId) : '',
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
            expiresAt: data.expiresAt,
            permissions: data.permissions || []
          };

          // API 키 처리
          if (includePasswords && data.apiKey) {
            decryptedData.apiKey = await decrypt(data.apiKey, userId);
          } else {
            decryptedData.apiKey = '****';
          }

          // 시크릿 키 처리
          if (includePasswords && data.secretKey) {
            decryptedData.secretKey = await decrypt(data.secretKey, userId);
          } else {
            decryptedData.secretKey = '****';
          }

          apiKeys.push(decryptedData);
        } catch (decryptError) {
          console.error('API 키 복호화 실패:', decryptError);
        }
      }

      return apiKeys;
    } catch (error) {
      console.error('API 키 내보내기 실패:', error);
      return [];
    }
  },

  /**
   * 보안 메모 데이터 내보내기
   */
  async exportSecureNotes(userId, includeAttachments = false) {
    try {
      const notesRef = collection(db, 'users', userId, 'secureNotes');
      const snapshot = await getDocs(notesRef);
      
      const secureNotes = [];
      
      for (const doc of snapshot.docs) {
        const data = doc.data();
        
        try {
          const decryptedData = {
            id: doc.id,
            title: data.title ? await decrypt(data.title, userId) : '',
            content: data.content ? await decrypt(data.content, userId) : '',
            category: data.category || 'general',
            tags: data.tags || [],
            pin: data.pin || '',
            createdAt: data.createdAt,
            updatedAt: data.updatedAt
          };

          // 첨부파일 처리
          if (includeAttachments && data.attachments && data.attachments.length > 0) {
            decryptedData.attachments = await this.exportNoteAttachments(data.attachments);
          } else {
            decryptedData.attachments = data.attachments ? 
              data.attachments.map(att => ({
                name: att.name,
                size: att.size,
                type: att.type,
                url: '[첨부파일은 별도 다운로드 필요]'
              })) : [];
          }

          secureNotes.push(decryptedData);
        } catch (decryptError) {
          console.error('보안 메모 복호화 실패:', decryptError);
        }
      }

      return secureNotes;
    } catch (error) {
      console.error('보안 메모 내보내기 실패:', error);
      return [];
    }
  },

  /**
   * 첨부파일 내보내기 (Base64 인코딩)
   */
  async exportNoteAttachments(attachments) {
    const exportedAttachments = [];
    
    for (const attachment of attachments) {
      try {
        if (attachment.url) {
          // Firebase Storage에서 파일 다운로드
          const blob = await getFileBlob(attachment.url);
          const base64Data = await this.blobToBase64(blob);
          
          exportedAttachments.push({
            name: attachment.name,
            size: attachment.size,
            type: attachment.type,
            data: base64Data
          });
        }
      } catch (error) {
        console.error('첨부파일 내보내기 실패:', error);
        exportedAttachments.push({
          name: attachment.name,
          size: attachment.size,
          type: attachment.type,
          error: '파일 다운로드 실패'
        });
      }
    }
    
    return exportedAttachments;
  },

  /**
   * Blob을 Base64로 변환
   */
  async blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64data = reader.result.split(',')[1];
        resolve(base64data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  },

  /**
   * JSON 형식으로 다운로드
   */
  async downloadAsJSON(exportData, isEncrypted = false) {
    const jsonString = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-');
    const suffix = isEncrypted ? '-encrypted' : '';
    const filename = `password-wallet-export${suffix}-${timestamp}.json`;
    
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  /**
   * CSV 형식으로 다운로드
   */
  async downloadAsCSV(exportData) {
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-');
    
    // 각 데이터 타입별로 CSV 생성
    if (exportData.data.passwords) {
      await this.downloadPasswordsCSV(exportData.data.passwords, timestamp);
    }
    
    if (exportData.data.creditCards) {
      await this.downloadCreditCardsCSV(exportData.data.creditCards, timestamp);
    }
    
    if (exportData.data.apiKeys) {
      await this.downloadApiKeysCSV(exportData.data.apiKeys, timestamp);
    }
    
    if (exportData.data.secureNotes) {
      await this.downloadSecureNotesCSV(exportData.data.secureNotes, timestamp);
    }
  },

  /**
   * 패스워드를 CSV로 다운로드
   */
  async downloadPasswordsCSV(passwords, timestamp) {
    const headers = ['사이트', '사용자명', '이메일', '패스워드', 'URL', '카테고리', '메모', '생성일', '수정일'];
    const csvContent = [
      headers.join(','),
      ...passwords.map(password => [
        this.escapeCsvValue(password.site || ''),
        this.escapeCsvValue(password.username || ''),
        this.escapeCsvValue(password.email || ''),
        this.escapeCsvValue(password.password || ''),
        this.escapeCsvValue(password.url || ''),
        this.escapeCsvValue(password.category || ''),
        this.escapeCsvValue(password.notes || ''),
        this.escapeCsvValue(password.createdAt || ''),
        this.escapeCsvValue(password.updatedAt || '')
      ].join(','))
    ].join('\n');

    this.downloadCSVFile(csvContent, `passwords-${timestamp}.csv`);
  },

  /**
   * 신용카드를 CSV로 다운로드
   */
  async downloadCreditCardsCSV(creditCards, timestamp) {
    const headers = ['카드소유자', '은행명', '카드타입', '카드번호', '만료일', 'CVV', 'PIN', '메모', '생성일'];
    const csvContent = [
      headers.join(','),
      ...creditCards.map(card => [
        this.escapeCsvValue(card.cardholderName || ''),
        this.escapeCsvValue(card.bankName || ''),
        this.escapeCsvValue(card.cardType || ''),
        this.escapeCsvValue(card.cardNumber || ''),
        this.escapeCsvValue(card.expiryDate || ''),
        this.escapeCsvValue(card.cvv || ''),
        this.escapeCsvValue(card.pin || ''),
        this.escapeCsvValue(card.notes || ''),
        this.escapeCsvValue(card.createdAt || '')
      ].join(','))
    ].join('\n');

    this.downloadCSVFile(csvContent, `credit-cards-${timestamp}.csv`);
  },

  /**
   * API 키를 CSV로 다운로드
   */
  async downloadApiKeysCSV(apiKeys, timestamp) {
    const headers = ['서비스명', '키이름', 'API키', '시크릿키', '설명', 'URL', '권한', '만료일', '메모'];
    const csvContent = [
      headers.join(','),
      ...apiKeys.map(apiKey => [
        this.escapeCsvValue(apiKey.serviceName || ''),
        this.escapeCsvValue(apiKey.keyName || ''),
        this.escapeCsvValue(apiKey.apiKey || ''),
        this.escapeCsvValue(apiKey.secretKey || ''),
        this.escapeCsvValue(apiKey.description || ''),
        this.escapeCsvValue(apiKey.url || ''),
        this.escapeCsvValue(apiKey.permissions?.join(';') || ''),
        this.escapeCsvValue(apiKey.expiresAt || ''),
        this.escapeCsvValue(apiKey.notes || '')
      ].join(','))
    ].join('\n');

    this.downloadCSVFile(csvContent, `api-keys-${timestamp}.csv`);
  },

  /**
   * 보안 메모를 CSV로 다운로드
   */
  async downloadSecureNotesCSV(secureNotes, timestamp) {
    const headers = ['제목', '내용', '카테고리', '태그', 'PIN', '첨부파일', '생성일', '수정일'];
    const csvContent = [
      headers.join(','),
      ...secureNotes.map(note => [
        this.escapeCsvValue(note.title || ''),
        this.escapeCsvValue(note.content || ''),
        this.escapeCsvValue(note.category || ''),
        this.escapeCsvValue(note.tags?.join(';') || ''),
        this.escapeCsvValue(note.pin || ''),
        this.escapeCsvValue(note.attachments?.map(att => att.name).join(';') || ''),
        this.escapeCsvValue(note.createdAt || ''),
        this.escapeCsvValue(note.updatedAt || '')
      ].join(','))
    ].join('\n');

    this.downloadCSVFile(csvContent, `secure-notes-${timestamp}.csv`);
  },

  /**
   * CSV 값 이스케이프 처리
   */
  escapeCsvValue(value) {
    const stringValue = String(value || '');
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  },

  /**
   * CSV 파일 다운로드
   */
  downloadCSVFile(csvContent, filename) {
    // UTF-8 BOM 추가 (Excel 한글 호환)
    const bom = '\uFEFF';
    const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
};